import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// ----------------------------------------------------------------------

// Custom filter function that searches through multiple fields
const createMultiFieldFilter = (fullData, searchFields, getOptionValue) => {
  return (options, { inputValue }) => {
    if (!inputValue) {
      return options;
    }

    const searchTerm = inputValue.toLowerCase().trim();

    // If fullData is provided, search through all specified fields
    if (fullData && searchFields && searchFields.length > 0) {
      return options.filter((option) => {
        // Find the full data object for this option
        let fullItem = null;
        
        if (getOptionValue) {
          // Use custom function to find the item
          fullItem = fullData.find((item) => getOptionValue(item) === option);
        } else {
          // Try to find by direct comparison or by common fields
          // First try direct match
          fullItem = fullData.find((item) => item === option);
          
          // If not found, try to find by matching the option with common field patterns
          if (!fullItem && typeof option === 'string') {
            // Try to find by matching with first search field (usually the ID field)
            const firstField = searchFields[0];
            if (firstField) {
              fullItem = fullData.find((item) => {
                const fieldValue = item[firstField];
                return fieldValue != null && String(fieldValue) === option;
              });
            }
          }
        }

        if (!fullItem) {
          // Fallback to searching the option itself
          return String(option).toLowerCase().includes(searchTerm);
        }

        // Search through all specified fields in the full item
        return searchFields.some((field) => {
          const fieldValue = fullItem[field];
          if (fieldValue == null) return false;
          return String(fieldValue).toLowerCase().includes(searchTerm);
        });
      });
    }

    // Default behavior: search through the option value itself
    return options.filter((option) =>
      String(option).toLowerCase().includes(searchTerm)
    );
  };
};

export default function RHFAutocomplete({
  name,
  label,
  placeholder,
  helperText,
  onClick,
  fullData,
  searchFields,
  getOptionValue,
  ...other
}) {
  const { control, setValue } = useFormContext();

  // Create custom filter if fullData and searchFields are provided
  const customFilterOptions = fullData && searchFields && searchFields.length > 0
    ? createMultiFieldFilter(fullData, searchFields, getOptionValue)
    : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          onChange={(event, newValue) => setValue(name, newValue, { shouldValidate: true })}
          {...(customFilterOptions && { filterOptions: customFilterOptions })}
          renderInput={(params) => (
            <TextField
              label={label}
              onClick={onClick}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error?.message : helperText}
              {...params}
            />
          )}
          {...other}
        />
      )}
    />
  );
}

RHFAutocomplete.propTypes = {
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  onClick: PropTypes.func,
  fullData: PropTypes.array, // Full data array with all fields (e.g., dropDownAssetNumber)
  searchFields: PropTypes.arrayOf(PropTypes.string), // Fields to search through (e.g., ['ast_mst_asset_no', 'ast_mst_asset_shortdesc'])
  getOptionValue: PropTypes.func, // Function to extract option value from fullData item (optional)
};
